import { useWorkshopMode } from "../../../../hooks/useWorkshopMode";
import React, { useState } from "react";
import { t } from "i18next";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { OrgScreenShell } from "../../../components/OrgShell/OrgScreenShell";
import { SectionNavItem } from "../../../components/OrgShell/SectionNavItem";
import { BadgeChip } from "../../../components/OrgShell/BadgeChip";
import {
  FieldGrid,
  FieldGroup,
  SummaryRow,
} from "../../../components/OrgShell/FieldGroup";
import {
  CommissionSummaryCard,
  formatCommission,
} from "../../../components/commission/commission-summary-card.component";
import { CommissionFormWithVendor } from "../../../components/commission/commission-form-with-vendor.component";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import {
  InputLabel,
  RequiredMark,
} from "../../../components/Input/InputLabel";
import { CustomInput } from "../../../components/Input/CustomInput";
import { CustomPhoneInput } from "../../../components/Input/CustomPhoneInput";
import {
  CountryAutoComplete,
  CountryAutoCompleteItem,
} from "../../../components/Input/CountryAutoComplete";
import Dropdown from "../../../components/dropdown/Dropdown";
import VATField from "../../../components/vat/VATField";
import LoadingElementIsolated from "../../../components/loading-element/loading-element-isolated";
import { useCountryContext } from "../../design-editor2/contexts/country.context";
import { CustomInputChangeEvent } from "../../../../utils/models/events/CustomInputChangeEvent";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { ShippingSection } from "../../customer-update/component/sections/shipping-section.component";
import { BrandsSection } from "../../customer-update/component/sections/brands-section.component";
import { useVisibleFieldErrors } from "../../customer-update/models/required-fields";
import { InputError } from "../../my-profile/InputError";
import { HidePricesToggle } from "./hide-prices-toggle.component";
import { CustomerForm } from "./customer.form";
import {
  CustomerCreateWizard_VM,
  WIZARD_STEPS,
} from "../vmHooks/useCustomerCreateFormViewModel";

export function CustomerCreateWizard(vm: CustomerCreateWizard_VM) {
  const {
    form,
    setForm,
    step,
    goToStep,
    goNext,
    goBack,
    maxVisitedStep,
    isFormValid,
    isLoading,
    onCreate,
    onCancel,
    shippingDiffers,
    setShippingDiffers,
  } = vm;

  const isLastStep = step === WIZARD_STEPS.length;

  return (
    <OrgScreenShell
      title={form.name || "New customer"}
      subtitle={`Step ${step} of ${WIZARD_STEPS.length} — ${
        WIZARD_STEPS[step - 1].label
      }`}
      avatarText={form.name ? undefined : "+"}
      badges={
        form.hidePrices ? (
          <BadgeChip tone="orange">Prices hidden</BadgeChip>
        ) : undefined
      }
      nav={WIZARD_STEPS.map((wizardStep) => (
        <SectionNavItem
          key={wizardStep.ordinal}
          label={wizardStep.label}
          ordinal={wizardStep.ordinal}
          done={
            wizardStep.ordinal < step && vm.isStepValid(wizardStep.ordinal)
          }
          active={step === wizardStep.ordinal}
          disabled={wizardStep.ordinal > maxVisitedStep}
          onClick={() => goToStep(wizardStep.ordinal)}
        />
      ))}
      footer={{
        onCancel,
        onSave: isLastStep ? () => void onCreate().catch(() => {}) : goNext,
        saveLabel: isLastStep ? "Create customer" : "Next",
        saveDisabled: isLastStep ? !isFormValid : false,
        error: step === 1 ? vm.validationSummary : undefined,
        extra:
          step > 1 ? (
            <SecondaryButton className="mr-4" onClick={goBack}>
              Back
            </SecondaryButton>
          ) : undefined,
      }}
      isLoading={isLoading}
    >
      {step === 1 && (
        <CompanyStep
          vm={vm}
          shippingDiffers={shippingDiffers}
          setShippingDiffers={setShippingDiffers}
        />
      )}
      {step === 2 && <ContactStep form={form} setForm={setForm} />}
      {step === 3 && <PricingStep vm={vm} />}
      {step === 4 && (
        <BrandsSection
          manufacturers={vm.manufacturers}
          isLoading={vm.isManufacturersLoading}
          excludedIds={form.excludedManufacturers.map((x) => x.id)}
          onChange={(ids) =>
            setForm({
              ...form,
              excludedManufacturers: (vm.manufacturers ?? []).filter((m) =>
                ids.includes(m.id)
              ),
            })
          }
        />
      )}
      {step === 5 && <ReviewStep vm={vm} />}
    </OrgScreenShell>
  );
}

// ----- Step 1: Company -----

function CompanyStep({
  vm,
  shippingDiffers,
  setShippingDiffers,
}: {
  vm: CustomerCreateWizard_VM;
  shippingDiffers: boolean;
  setShippingDiffers: (differs: boolean) => void;
}) {
  const { form, setForm, showVendorPicker, vendors, selectedVendor } = vm;
  const { errorFor, handleBlur, markTouched } = useVisibleFieldErrors(
    vm.fieldErrors,
    vm.showFieldErrors
  );

  function handleInput(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    setForm({ ...form, [target.name]: target.value });
  }

  function handleCountryChange(country: CountryAutoCompleteItem) {
    setForm({ ...form, country: country.code });
  }

  function errorClass(field: string): string | undefined {
    return errorFor(field) ? "!border-red-100" : undefined;
  }

  return (
    <div className="space-y-8">
      {showVendorPicker && vendors && (
        <FieldGroup
          title="Vendor"
          description="Admins must choose which vendor the customer belongs to."
        >
          <div className="w-72">
            <Dropdown
              selectedOption={selectedVendor}
              options={vendors}
              defaultTitle={t("pick-vendor")!}
              getOptionDisplay={(v) => v.name}
              onOptionSelect={(o) => {
                if (!o) return;
                setForm({ ...form, parentId: o.id });
              }}
            />
          </div>
        </FieldGroup>
      )}

      <FieldGroup title="Company details">
        <FieldGrid>
          <InputWrapper>
            <InputLabel>
              Company name
              <RequiredMark />
            </InputLabel>
            <CustomInput
              name="name"
              value={form.name}
              onChange={handleInput}
              onBlur={handleBlur}
              className={errorClass("name")}
            />
            <InputError errorMessage={errorFor("name")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              CVR
              <RequiredMark />
            </InputLabel>
            <CustomInput
              name="cvr"
              value={form.cvr}
              onChange={handleInput}
              onBlur={handleBlur}
              className={errorClass("cvr")}
            />
            <InputError errorMessage={errorFor("cvr")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              Company email
              <RequiredMark />
            </InputLabel>
            <CustomInput
              type="email"
              name="email"
              value={form.email}
              onChange={handleInput}
              onBlur={handleBlur}
              className={errorClass("email")}
            />
            <InputError errorMessage={errorFor("email")} />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>
              Company phone
              <RequiredMark />
            </InputLabel>
            <CustomPhoneInput
              name="phone"
              value={form.phone}
              onChange={handleInput}
              onBlur={handleBlur}
              disabled={false}
              className={errorClass("phone")}
            />
            <InputError errorMessage={errorFor("phone")} />
          </InputWrapper>
          <InputWrapper>
            {/* EORI is optional — no required mark, no validation. */}
            <InputLabel>EORI</InputLabel>
            <CustomInput name="eori" value={form.eori} onChange={handleInput} />
          </InputWrapper>
        </FieldGrid>
      </FieldGroup>

      <FieldGroup title="Address">
        <FieldGrid>
          <InputWrapper>
            <InputLabel>
              Address
              <RequiredMark />
            </InputLabel>
            <CustomInput
              name="address"
              value={form.address}
              onChange={handleInput}
              onBlur={handleBlur}
              className={errorClass("address")}
            />
            <InputError errorMessage={errorFor("address")} />
          </InputWrapper>
          <div className="grid grid-cols-2 gap-x-4">
            <InputWrapper>
              <InputLabel>
                Postcode
                <RequiredMark />
              </InputLabel>
              <CustomInput
                name="postCode"
                value={form.postCode}
                onChange={handleInput}
                onBlur={handleBlur}
                className={errorClass("postCode")}
              />
              <InputError errorMessage={errorFor("postCode")} />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>
                City
                <RequiredMark />
              </InputLabel>
              <CustomInput
                name="city"
                value={form.city}
                onChange={handleInput}
                onBlur={handleBlur}
                className={errorClass("city")}
              />
              <InputError errorMessage={errorFor("city")} />
            </InputWrapper>
          </div>
          <InputWrapper>
            <InputLabel>
              Country
              <RequiredMark />
            </InputLabel>
            <div data-field="country" onBlur={() => markTouched("country")}>
              <CountryAutoComplete
                disabled={false}
                selectedCode={form.country}
                onCountryChange={handleCountryChange}
                error={!!errorFor("country")}
              />
            </div>
            <InputError errorMessage={errorFor("country")} />
          </InputWrapper>
        </FieldGrid>
      </FieldGroup>

      <ShippingSection
        form={form}
        onChange={(patch) => setForm({ ...form, ...patch } as CustomerForm)}
        differs={shippingDiffers}
        onDiffersChange={setShippingDiffers}
        required
        errors={vm.fieldErrors}
        showErrors={vm.showFieldErrors}
      />
    </div>
  );
}

// ----- Step 2: Contact & first user -----

function ContactStep({
  form,
  setForm,
}: {
  form: CustomerForm;
  setForm: (form: CustomerForm) => void;
}) {
  function handleInput(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent
  ) {
    const target = event.target;
    setForm({ ...form, [target.name]: target.value });
  }

  return (
    <div className="space-y-8">
      <FieldGroup
        title="Primary contact & first user"
        description="This person becomes the customer's primary contact and their first user, and receives a welcome email."
      >
        <FieldGrid>
          <InputWrapper>
            <InputLabel>Contact person</InputLabel>
            <CustomInput
              name="contactPerson"
              value={form.contactPerson}
              onChange={handleInput}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Contact email</InputLabel>
            <CustomInput
              type="email"
              name="contactEmail"
              value={form.contactEmail}
              onChange={handleInput}
            />
          </InputWrapper>
          <InputWrapper>
            <InputLabel>Contact phone</InputLabel>
            <CustomPhoneInput
              name="contactPhone"
              value={form.contactPhone}
              onChange={handleInput}
              disabled={false}
            />
          </InputWrapper>
        </FieldGrid>
        <p className="text-xs text-gray-500">
          You can add more users after the customer is created.
        </p>
      </FieldGroup>
    </div>
  );
}

// ----- Step 3: Pricing -----

function PricingStep({ vm }: { vm: CustomerCreateWizard_VM }) {
  const { form, setForm, showVendorPicker } = vm;
  const { profile } = useProfileContext();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const workshopMode = useWorkshopMode();

  if (workshopMode) {
    return (
      <p className="text-sm text-gray-500">
        The customer starts with your organization&apos;s default pricing.
        Price settings are hidden for workshop users.
      </p>
    );
  }

  const commissionVendorId =
    form.parentId ?? profile?.userOrganizationInformations.organizationId;

  return (
    <div className="space-y-8">
      <FieldGroup
        title="Commission"
        description="New customers start with your default commission profile."
      >
        {showVendorPicker && !form.parentId ? (
          <p className="text-xs text-gray-500">
            Select a vendor in step 1 before choosing commissions.
          </p>
        ) : (
          <>
            <CommissionSummaryCard
              title={
                vm.isCommissionCustomized ? "Customized" : "Your defaults"
              }
              commission={vm.commissionProfile}
              action={
                <SecondaryButton onClick={() => setCustomizeOpen(!customizeOpen)}>
                  {customizeOpen ? "Close" : "Customize"}
                </SecondaryButton>
              }
            />
            {customizeOpen &&
              (vm.logoOptions ? (
                <div className="rounded-md border p-4">
                  <CommissionFormWithVendor
                    updateCommissions={vm.updateCommission}
                    logoOptions={vm.logoOptions}
                    commissionProfile={vm.commissionProfile}
                    userType={"customer"}
                    vendorId={commissionVendorId!}
                  />
                </div>
              ) : (
                <div className="relative h-24">
                  <LoadingElementIsolated transparent />
                </div>
              ))}
          </>
        )}
      </FieldGroup>

      <FieldGroup
        title="VAT"
        description="Prefilled with your default VAT rate."
      >
        <VATField
          value={form.vatRate ?? 0}
          onChange={(value) => setForm({ ...form, vatRate: value })}
        />
      </FieldGroup>

      <FieldGroup title="Price visibility">
        <HidePricesToggle
          checked={form.hidePrices ?? false}
          onChange={(next) => setForm({ ...form, hidePrices: next })}
          className="flex flex-col space-y-1"
        />
      </FieldGroup>
    </div>
  );
}

// ----- Step 5: Review & create -----

function ReviewStep({ vm }: { vm: CustomerCreateWizard_VM }) {
  const { form, goToStep, shippingDiffers } = vm;
  const { countries } = useCountryContext();

  function countryName(code?: string): string {
    if (!code) return "—";
    return countries.find((x) => x.code === code)?.name ?? code;
  }

  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500">
        Review the new customer below. Everything can be changed later from
        the customer&apos;s page.
      </p>

      <ReviewCard title="Company" onEdit={() => goToStep(1)}>
        <SummaryRow label="Name">{form.name || "—"}</SummaryRow>
        <SummaryRow label="CVR">{form.cvr || "—"}</SummaryRow>
        <SummaryRow label="Email">{form.email || "—"}</SummaryRow>
        <SummaryRow label="Phone">{form.phone || "—"}</SummaryRow>
        <SummaryRow label="Address">
          {[form.address, form.postCode, form.city]
            .filter(Boolean)
            .join(", ") || "—"}
        </SummaryRow>
        <SummaryRow label="Country">{countryName(form.country)}</SummaryRow>
        {form.eori && <SummaryRow label="EORI">{form.eori}</SummaryRow>}
        <SummaryRow label="Shipping">
          {shippingDiffers
            ? [
                form.shippingName,
                form.shippingAddress,
                form.shippingPostCode,
                form.shippingCity,
                countryName(form.shippingCountry),
              ]
                .filter(Boolean)
                .join(", ") || "—"
            : "Same as company address"}
        </SummaryRow>
      </ReviewCard>

      <ReviewCard title="Contact & first user" onEdit={() => goToStep(2)}>
        <SummaryRow label="Contact person">
          {form.contactPerson || "—"}
        </SummaryRow>
        <SummaryRow label="Email">{form.contactEmail || "—"}</SummaryRow>
        <SummaryRow label="Phone">{form.contactPhone || "—"}</SummaryRow>
      </ReviewCard>

      <ReviewCard title="Pricing" onEdit={() => goToStep(3)}>
        <SummaryRow label="Commission">
          {vm.isCommissionCustomized
            ? "Customized"
            : vm.vendorDefaultCommission
            ? "Your defaults"
            : "None"}{" "}
          ({formatCommission(vm.commissionProfile?.overAll)} overall)
        </SummaryRow>
        <SummaryRow label="VAT">{form.vatRate ?? 0}%</SummaryRow>
        <SummaryRow label="Prices">
          {form.hidePrices ? (
            <BadgeChip tone="orange">Prices hidden</BadgeChip>
          ) : (
            "Visible"
          )}
        </SummaryRow>
      </ReviewCard>

      <ReviewCard title="Brands" onEdit={() => goToStep(4)}>
        <SummaryRow label="Excluded brands">
          {form.excludedManufacturers.length > 0
            ? form.excludedManufacturers.map((x) => x.name).join(", ")
            : "None — all brands visible"}
        </SummaryRow>
      </ReviewCard>
    </div>
  );
}

function ReviewCard({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border bg-gray-150 p-4">
      <div className="mb-2 flex items-center">
        <h4 className="flex-1 text-sm font-semibold text-gray-700">{title}</h4>
        <button
          type="button"
          className="text-xs font-medium text-primary-100 hover:underline"
          onClick={onEdit}
        >
          Edit
        </button>
      </div>
      {children}
    </div>
  );
}
